import * as React from 'react';
import { useCallback, useContext } from 'react';
import {
  GetWorkspaceSourcesQuery,
  GetWorkspaceSourcesQueryVariables,
  GetWorkspaceSourcesDocument,
  ImportSourceArgsGroupInput,
  useCreateImportSourceMutation,
  useUpdateImportSourceMutation
} from '../../../../models/graphql';
import { AddEditSourceModalProps, AddEditSourceModalView } from './AddEditSourceModal.view';
import { TreeMenuContext } from '../TreeMenu/TreeMenuProvider';
import { AppContext } from '../../../../providers/AppProvider';

export type AddEditSourceModalApolloProps = {
  closeModalHandler: CallableFunction;
  args?: ImportSourceArgsGroupInput;
};

export const AddEditSourceModalApollo = (props: AddEditSourceModalApolloProps): JSX.Element => {
  const { args, closeModalHandler } = props;
  const { reFetchTreeMenu } = useContext(TreeMenuContext);
  const { selectedWorkspaceId } = useContext(AppContext);
  const [createImportSource] = useCreateImportSourceMutation();
  const [updateImportSource] = useUpdateImportSourceMutation();

  const createSourceHandler = useCallback(
    async (name: string) => {
      if (selectedWorkspaceId && args) {
        await createImportSource({
          variables: {
            workspaceId: selectedWorkspaceId,
            input: {
              name: name,
              args: {
                ...args
              }
            }
          },
          update: (cache, result) => {
            // if we don't have, then something is wrong so return
            if (!selectedWorkspaceId || !result.data?.createImportSource) {
              return;
            }

            const data = cache.readQuery<GetWorkspaceSourcesQuery, GetWorkspaceSourcesQueryVariables>({
              query: GetWorkspaceSourcesDocument,
              variables: { workspaceId: selectedWorkspaceId }
            });

            if (!data || !data.workspace.importSources) {
              return;
            }

            const newData: GetWorkspaceSourcesQuery = {
              workspace: {
                ...data.workspace,
                importSources: [...data.workspace.importSources, ...[result.data.createImportSource]]
              }
            };

            cache.writeQuery({
              query: GetWorkspaceSourcesDocument,
              variables: { workspaceId: selectedWorkspaceId },
              data: newData
            });

            reFetchTreeMenu();
          }
        });
      }
    },
    [args, createImportSource, reFetchTreeMenu, selectedWorkspaceId]
  );

  const updateSourceHandler = useCallback(
    async (id: string, name: string) => {
      await updateImportSource({
        variables: { importSourceId: id, input: { name } },
        update: () => {
          reFetchTreeMenu();
        }
      });
    },
    [reFetchTreeMenu, updateImportSource]
  );

  const viewProps: AddEditSourceModalProps = {
    closeModalHandler,
    createSourceHandler,
    updateSourceHandler
  };

  return <AddEditSourceModalView {...viewProps} />;
};
